import {inject, injectable} from 'inversify';

import type {
  MembershipApiModel,
  MembershipExpandedCommon,
} from '../../../../../api/membership';
import type {ApiListResponseExpanded, Expand} from '../../../../../api/types';
import type {CoreCoachMembershipsListParams} from '../../../../../api/types/core/methods';
import type {CoreMembershipApiModel} from '../../../../../api/types/core/models';
import type {ApiRpcQuery} from '../../../../../api/utils/apiRequest';
import type {MembershipModel} from '../../../../domain/Membership/Membership.model';
import type {FetchMembershipListQuery} from '../FetchMembershipList.query';

import {apiRequest} from '../../../../../api/utils/apiRequest';
import {expandObj} from '../../../../../api/utils/expand';
import {MembershipMapperJrpc} from '../../../../domain/Membership/mappers/Membership.mapper.jrpc';
import {CurrentUserService} from '../../../../system/currentUser/CurrentUser.service';

@injectable()
export class FetchMembershipListQueryJrpc implements FetchMembershipListQuery {
  constructor(
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(MembershipMapperJrpc)
    private readonly mapper: MembershipMapperJrpc,
  ) {}

  async exec(
    props: FetchMembershipListQuery.Props,
  ): Promise<MembershipModel[]> {
    const expand = this.mapper.expand.getByEnv();

    const result = this.currentUserService.user?.isCoach
      ? await this.fetchAsCoach(props, expand)
      : await this.fetchAsClient(props, expand);

    if (result._items.length) {
      const expanded = result._items.map(membership =>
        expandObj<MembershipApiModel, MembershipExpandedCommon>(membership, {
          expand,
          expanded: result._expanded,
        }),
      );

      return expanded.map(membership => this.mapper.mapDataToModel(membership));
    }

    return [];
  }

  private buildQuery(props: FetchMembershipListQuery.Props) {
    const {courseId, excludedStatuses, userId} = props;

    // Construct the base query with mandatory course_id
    const result = [
      ['course_id', '==', courseId],
    ] as ApiRpcQuery<CoreMembershipApiModel>[];

    // Add user_id filter if provided
    if (userId) {
      result.push(['user_id', '==', userId]);
    }

    // Add status filter if provided, excluding the specified statuses
    if (excludedStatuses) {
      result.push(['status', '!in', excludedStatuses]);
    }

    return result;
  }

  private async fetchAsClient(
    props: FetchMembershipListQuery.Props,
    expand: Expand,
  ): Promise<ApiListResponseExpanded<MembershipApiModel>> {
    return apiRequest<CoreCoachMembershipsListParams>({
      method: 'client.memberships.list',
      params: {
        expand,
        query: this.buildQuery(props),
        sort: [['created', -1]],
      },
    });
  }

  private async fetchAsCoach(
    props: FetchMembershipListQuery.Props,
    expand: Expand,
  ): Promise<ApiListResponseExpanded<MembershipApiModel>> {
    return apiRequest<CoreCoachMembershipsListParams>({
      method: 'coach.memberships.list',
      params: {
        expand,
        query: this.buildQuery(props),
        sort: [['created', -1]],
      },
    });
  }
}
